import { Breadcrumbs, Button, Heading, Layout } from '@design-systems/canvas-ui'
import { createContext, useMemo } from 'react'
import Icon, {
    CloseIcon,
    ExternalIcon,
    GearIcon,
    LogoutIcon,
    MenuIcon,
    NotebookIcon,
    SearchIcon,
    StoreIcon,
} from '@design-systems/react-icons'
import { useState } from 'react'
import { useAuth } from '@praxis/component-auth'
import {
    Link,
    useLocation,
    useNavigate,
    useSearchParams,
} from 'react-router-dom'
import './layout.css'
import { isCompany, isStore as isStoreCheck } from 'utils/location'
import { useUser } from 'components/user-context/user-context'
import { useFlags } from '../../firebase/FirebaseRemoteConfig.provider'
import { UrlParams } from 'enums/url-params'
import { getBreadcrumbs, getHeaderLocation } from './helpers'
import { useLocationHierarchy } from 'hooks/locationHierarchy/useLocationHierarchy'
import { useMPMEnv } from 'utils/env'
import usePullDown from 'hooks/utils/usePullDown'
import { useLogClick } from 'utils/analytics'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useDeviceInfo } from 'hooks/utils/useDeviceInfo'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import FeedbackFooter from 'pages/feedback/feedback'
import { useBanner } from 'hooks/banners/useBanners'
import { BannerAlert } from 'components/banner-alert/banner-alert'
import { useHeaderOffset } from 'hooks/utils/useCalculateHeader'
import { useRouteAccess } from 'hooks/utils/useRouteAccess'

export interface ILayoutProps {
    heading?: string
    children?: JSX.Element | JSX.Element[]
    isBottomNavDisplayed?: boolean
    topNavRightButton?: JSX.Element | null
    className?: string
    breadcrumbsEnabled?: boolean
    isMenuOverride?: boolean
    menuOverride?: JSX.Element | JSX.Element[]
    tabs?: React.ReactNode
    hideFeedback?: boolean
    /**
     * Whether this page renders PageTabs beneath the header. Used together
     * with breadcrumbs to decide the navbar's bottom padding.
     *
     * Declared by the caller rather than detected from the DOM: PageTabs is
     * sometimes conditional. Pass the tab group via `tabs` so it appears
     * immediately before any banners, ahead of page content.
     */
    hasTabs?: boolean
}

export const BannerOffsetContext = createContext<number>(0)

export const MPMLayout = ({
    heading,
    children,
    topNavRightButton,
    className,
    breadcrumbsEnabled = true,
    isMenuOverride = false,
    menuOverride = <></>,
    tabs,
    hideFeedback = false,
    hasTabs = false,
}: ILayoutProps) => {
    const { userAgent, t, i18n } = useEssentials()
    const { greenfieldLinkEnabled } = useFlags()
    let { isZebraDevice } = useDeviceInfo(userAgent)
    const {
        sales,
        assetsProtection,
        insights,
        loyalty,
        payroll,
        itemSearch,
        safety,
        shortage,
        storeFacts,
        notes,
        trailer,
        settings,
        brands,
        storeFulfillment,
        operationalMetrics,
        layout,
        powerBI,
    } = TRANSLATION_KEYS
    const [params] = useSearchParams()
    const [isSideNavOpen, setIsSideNavOpen] = useState(false)
    const { logout } = useAuth()
    const navigate = useNavigate()
    const useLocationHook = useLocation()
    const navigateToScreen = (route: string) => {
        void navigate(`${route}${useLocationHook.search}`)
    }
    const { logClick } = useLogClick('layout')

    const { location } = useUser()
    const env = useMPMEnv()
    const { session } = useAuth()
    const currentURL = window.location.pathname
    const canAccessRoute = useRouteAccess()

    const visibleBanners = useBanner()
    useHeaderOffset()

    let currentLocation =
        params.get(UrlParams.LOCATION_ID) ?? location.shortName

    const isStore = isStoreCheck(currentLocation)
    const normalizedLocation = isCompany(currentLocation)
        ? 'Company'
        : currentLocation

    const { data: locationData } = useLocationHierarchy({
        env,
        accessToken: session?.accessToken ?? '',
        location: normalizedLocation,
        enabled: Boolean(normalizedLocation),
    })

    const locationName = locationData?.location_hierarchy?.location_name ?? ''
    const currentHeading = isStore
        ? `${locationName} ${currentLocation}`
        : getHeaderLocation(currentLocation)

    const childrenContainerClasses = className
        ? `main-content-container ${className}`
        : 'main-content-container'

    const searchAriaLabel = t(layout.searchAriaLabel)
    const navMenuAriaLabel = t(layout.navMenuAriaLabel)
    const navMenuCloseAriaLabel = t(
        layout.navMenuCloseAriaLabel || layout.navMenuAriaLabel,
    )

    const locationSearchButton = (
        <Button
            isIconOnly
            className="navbar-menu-button"
            aria-label={searchAriaLabel}
            aria-labelledby="location-search-label"
            onClick={() => {
                void navigate('/location-search', {
                    state: {
                        previousLocationPathname:
                            window.location.pathname + window.location.search,
                    },
                })
                logClick('nav location search')
            }}
            variant="ghost"
        >
            <span
                id="location-search-label"
                lang={i18n.language}
                className="hc8-sr-only"
                aria-hidden="true"
            >
                {searchAriaLabel}
            </span>
            <Icon
                svg={SearchIcon}
                className="navbar-icon"
                size="lg"
                aria-hidden
            />
        </Button>
    )

    let breadcrumbList: {
        label: string
        link: string
    }[] = useMemo(() => [], [])
    let breadcrumbHTML: React.ReactNode[] = []
    if (locationData?.location_hierarchy && breadcrumbsEnabled) {
        breadcrumbList = getBreadcrumbs({
            location,
            currentLocation,
            locationData: locationData,
        })
        breadcrumbHTML = breadcrumbList.map((item, index) => {
            if (index === breadcrumbList.length - 1) {
                return (
                    <Breadcrumbs.Item
                        className="current-breadcrumb"
                        key={item.label}
                    >
                        {item.label}
                    </Breadcrumbs.Item>
                )
            }
            return (
                <Breadcrumbs.Item key={item.label} as={'span'}>
                    <Link to={item.link}>{item.label}</Link>
                </Breadcrumbs.Item>
            )
        })
    }

    const hasBreadcrumbs = breadcrumbHTML.length > 0
    const adjusted = hasBreadcrumbs && hasTabs
    const adjustedClass = 'adjust-pb'

    const { handleTouchStart, handleTouchMove, handleTouchEnd } = usePullDown()

    const closeSideNav = () => setIsSideNavOpen(false)

    // Renders a single nav item button inside the Canvas 8-migrated drawer.
    // Uses a plain <button> (via Canvas 8 Button) to avoid adding a role="tab"
    // or other implicit role that conflicts with the nav landmark.
    const NavItem = ({
        label,
        isActive,
        onClick,
        icon,
    }: {
        label: string
        isActive?: boolean
        onClick: () => void
        // Canvas v8 icons from @design-systems/react-icons are React.FunctionComponent<null>.
        // Always render them via <Icon svg={...} />.

        icon?: React.FunctionComponent<null>
    }) => (
        <Button
            variant="ghost-neutral"
            className={`mpm-sidenav__item${isActive ? ' mpm-sidenav__item--active' : ''}`}
            onClick={onClick}
            aria-current={isActive ? 'page' : undefined}
        >
            {icon && <Icon svg={icon} aria-hidden />}
            {label}
        </Button>
    )

    return (
        <div
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div
                className={`navbar-container ${adjusted ? adjustedClass : ''}`}
            >
                {isMenuOverride ? (
                    <>{menuOverride}</>
                ) : (
                    <Button
                        isIconOnly
                        className="navbar-menu-button"
                        aria-label={navMenuAriaLabel}
                        aria-labelledby="nav-menu-label"
                        aria-expanded={isSideNavOpen}
                        onClick={() => {
                            logClick('nav menu open')
                            setIsSideNavOpen(true)
                        }}
                        variant="ghost"
                    >
                        <span
                            id="nav-menu-label"
                            lang={i18n.language}
                            className="hc8-sr-only"
                            aria-hidden="true"
                        >
                            {navMenuAriaLabel}
                        </span>
                        <Icon
                            svg={MenuIcon}
                            className="navbar-icon"
                            size="lg"
                            aria-hidden
                        />
                    </Button>
                )}
                <div className="navbar-heading">
                    {heading && (
                        <>
                            <Heading
                                className="kpi-heading hc8-fs-350"
                                tabIndex={0}
                            >
                                {heading}
                            </Heading>
                            <Heading
                                className="location-heading hc8-fs-050 hc8-lh-headline"
                                tabIndex={0}
                                data-testid="layout-current-heading"
                            >
                                {currentHeading}
                            </Heading>
                        </>
                    )}
                </div>
                <div className="navbar-container-right">
                    {!topNavRightButton ? (
                        <>{locationSearchButton}</>
                    ) : (
                        topNavRightButton
                    )}
                </div>
                {breadcrumbHTML.length > 0 && (
                    <Breadcrumbs className="breadcrumbs-container">
                        {breadcrumbHTML}
                    </Breadcrumbs>
                )}
            </div>
            {!hideFeedback && <FeedbackFooter />}
            {/* Scrim — dims page content and closes drawer on tap outside */}
            {isSideNavOpen && (
                <div
                    className="mpm-sidenav-overlay"
                    aria-hidden="true"
                    onClick={closeSideNav}
                />
            )}
            {/* Side navigation drawer. Canvas 8's Drawer component only
                supports location="right"|"bottom"; MPM's nav slides in from
                the left, so we use a custom <nav> styled with Canvas 8 tokens.
                The Drawer's panel, scrim, focus-trap, and close-button
                behaviour is replicated below with minimal custom CSS. */}
            <nav
                id="mpm-sidenav"
                className={`mpm-sidenav${isSideNavOpen ? ' mpm-sidenav--open' : ''}`}
                aria-label={t(layout.appTitle)}
                aria-hidden={!isSideNavOpen}
            >
                {isSideNavOpen && (
                    <>
                        <div className="mpm-sidenav__header">
                            <Heading size={5}>{t(layout.appTitle)}</Heading>
                            <Button
                                isIconOnly
                                aria-label={navMenuCloseAriaLabel}
                                variant="ghost"
                                onClick={closeSideNav}
                                className="mpm-sidenav__close-btn"
                            >
                                <span className="hc8-sr-only">
                                    {navMenuCloseAriaLabel}
                                </span>
                                <Icon svg={CloseIcon} aria-hidden />
                            </Button>
                        </div>
                        <div className="mpm-sidenav__nav">
                            {isStore && canAccessRoute('/insights') && (
                                <NavItem
                                    label={t(insights.title)}
                                    isActive={currentURL.startsWith(
                                        '/insights',
                                    )}
                                    onClick={() => {
                                        logClick('nav insights')
                                        navigateToScreen('/insights/yesterday')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/sales') && (
                                <NavItem
                                    label={t(sales.title)}
                                    isActive={currentURL.startsWith('/sales')}
                                    onClick={() => {
                                        logClick('nav sales')
                                        navigateToScreen('/sales/overview')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/payroll') && (
                                <NavItem
                                    label={t(payroll.title)}
                                    isActive={currentURL.startsWith('/payroll')}
                                    onClick={() => {
                                        logClick('nav payroll')
                                        navigateToScreen(
                                            isStore
                                                ? '/payroll/monthly'
                                                : '/payroll/location',
                                        )
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/mytarget-performance') && (
                                <NavItem
                                    label={'myTarget Performance'}
                                    isActive={currentURL.startsWith(
                                        '/mytarget-performance',
                                    )}
                                    onClick={() => {
                                        logClick('nav mytarget performance')
                                        navigateToScreen(
                                            '/mytarget-performance/guest',
                                        )
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/safety') && (
                                <NavItem
                                    label={t(safety.title)}
                                    isActive={currentURL.startsWith('/safety')}
                                    onClick={() => {
                                        logClick('nav safety')
                                        navigateToScreen('/safety/lagging')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/shortage') && (
                                <NavItem
                                    label={t(shortage.title)}
                                    isActive={currentURL === '/shortage'}
                                    onClick={() => {
                                        logClick('nav shortage')
                                        navigateToScreen('/shortage')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/assets-protection') && (
                                <NavItem
                                    label={t(assetsProtection.title)}
                                    isActive={currentURL.startsWith(
                                        '/assets-protection',
                                    )}
                                    onClick={() => {
                                        logClick('nav assets protection')
                                        navigateToScreen(
                                            '/assets-protection/payroll',
                                        )
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/loyalty') && (
                                <NavItem
                                    label={t(loyalty.title)}
                                    isActive={currentURL.startsWith('/loyalty')}
                                    onClick={() => {
                                        logClick('nav loyalty')
                                        navigateToScreen('/loyalty/overview')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/trailer') && (
                                <NavItem
                                    label={t(trailer.title)}
                                    isActive={currentURL.startsWith('/trailer')}
                                    onClick={() => {
                                        logClick('nav trailer')
                                        navigateToScreen(
                                            isStore
                                                ? '/trailer/summary'
                                                : '/trailer',
                                        )
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/brands') && (
                                <NavItem
                                    label={t(brands.title)}
                                    isActive={currentURL === '/brands'}
                                    onClick={() => {
                                        logClick('nav brands')
                                        navigateToScreen('/brands')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/store-fulfillment') && (
                                <NavItem
                                    label={t(storeFulfillment.title)}
                                    isActive={currentURL.includes(
                                        '/store-fulfillment',
                                    )}
                                    onClick={() => {
                                        logClick('nav store fulfillment')
                                        navigateToScreen(
                                            '/store-fulfillment/pickup',
                                        )
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/operations') && (
                                <NavItem
                                    label={t(operationalMetrics.title)}
                                    isActive={currentURL.startsWith(
                                        '/operations',
                                    )}
                                    onClick={() => {
                                        logClick('nav operations')
                                        navigateToScreen('/operations')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/item-search') && (
                                <NavItem
                                    label={t(itemSearch.searchLabel)}
                                    isActive={currentURL === '/item-search'}
                                    onClick={() => {
                                        logClick('nav item search')
                                        navigateToScreen('/item-search')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/power-bi') && (
                                <NavItem
                                    label={t(powerBI.title)}
                                    isActive={currentURL.startsWith(
                                        '/power-bi',
                                    )}
                                    onClick={() => {
                                        logClick('nav power bi')
                                        navigateToScreen('/power-bi')
                                        closeSideNav()
                                    }}
                                />
                            )}
                        </div>
                        <div className="mpm-sidenav__footer">
                            {canAccessRoute('/notes') && (
                                <NavItem
                                    label={t(notes.notes)}
                                    icon={NotebookIcon}
                                    isActive={currentURL === '/notes'}
                                    onClick={() => {
                                        logClick('nav notes')
                                        navigateToScreen('/notes')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {canAccessRoute('/store-facts') && (
                                <NavItem
                                    label={t(storeFacts.title)}
                                    icon={StoreIcon}
                                    isActive={currentURL === '/store-facts'}
                                    onClick={() => {
                                        logClick('nav store facts')
                                        navigateToScreen('/store-facts')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            {greenfieldLinkEnabled && !isZebraDevice && (
                                <Button
                                    as="a"
                                    href="https://greenfield.target.com"
                                    variant="ghost-neutral"
                                    className="mpm-sidenav__item"
                                    onClick={() => logClick('nav greenfield')}
                                >
                                    <Icon svg={ExternalIcon} aria-hidden />
                                    {t(layout.greenfieldLinkTitle)}
                                </Button>
                            )}
                            {canAccessRoute('/settings', false) && (
                                <NavItem
                                    label={t(settings.title)}
                                    icon={GearIcon}
                                    isActive={currentURL === '/settings'}
                                    onClick={() => {
                                        logClick('nav settings')
                                        navigateToScreen('/settings')
                                        closeSideNav()
                                    }}
                                />
                            )}
                            <NavItem
                                label={t(layout.logOutCTA)}
                                icon={LogoutIcon}
                                onClick={() => {
                                    logClick('nav logout')
                                    logout()
                                }}
                            />
                        </div>
                    </>
                )}
            </nav>
            <Layout className="mpm-layout">
                <Layout.Body className={childrenContainerClasses}>
                    {tabs}
                    {visibleBanners.length > 0 && (
                        <div
                            className="hc8-mh-2x"
                            data-testid="banner-alerts-container"
                        >
                            {visibleBanners.map((banner) => (
                                <BannerAlert
                                    key={`${banner.pageUrl}_${banner.startsAtUTC}_${banner.expiresAtUTC}`}
                                    className="banner-alert hc8-mv-2x"
                                    heading={banner?.title}
                                    storageKey={`${banner.pageUrl}_banner_${banner.expiresAtUTC}`}
                                    linkUrl={banner?.linkUrl}
                                    linkText={banner?.linkText}
                                >
                                    {banner?.message}
                                </BannerAlert>
                            ))}
                        </div>
                    )}
                    {children}
                </Layout.Body>
            </Layout>
        </div>
    )
}
